import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyRound, MapPin, Globe2, UtensilsCrossed } from "lucide-react";
import { apiClient } from "@/lib/api";
import { formatGNF, STATUS_LABEL } from "@/utils/format";
import { BrandLoader } from "@/components/BrandLoader";
import DeliveryMap from "@/components/DeliveryMap";

const STEPS = ["pending", "accepted", "preparing", "ready", "delivering", "delivered"];

// Distance haversine (km) + ETA scooter ~22 km/h (mêmes hypothèses que le suivi client)
const toKm = (a, b) => {
  const R = 6371, dLat = (b[0] - a[0]) * Math.PI / 180, dLng = (b[1] - a[1]) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * Math.PI / 180) * Math.cos(b[0] * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};
const fmtKm = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);
const etaMin = (km) => Math.max(2, Math.round((km / 22) * 60));

/** Public tracking page (no login) for a recipient in Conakry.
 *  The diaspora payer shares this link (WhatsApp). Shows the 4-digit code. */
export default function TrackShared() {
  const { t, i18n } = useTranslation();
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      apiClient.get(`/orders/track/${token}`)
        .then(({ data }) => { if (alive) setOrder(data); })
        .catch(() => { if (alive) setError(t("ts.invalidLink")); });
    };
    load();
    const iv = setInterval(load, 15000); // refresh status every 15s
    return () => { alive = false; clearInterval(iv); };
  }, [token]);

  if (error) {
    return <div className="max-w-lg mx-auto px-4 py-24 text-center text-mute" data-testid="track-error">{error}</div>;
  }
  if (!order) {
    return <BrandLoader message={t("ts.loading")} />;
  }

  const stepIdx = STEPS.indexOf(order.status);

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-10" data-testid="track-shared-page">
      <div className="text-xs uppercase tracking-[0.25em] text-brand-red mb-1">{t("ts.title")}</div>
      <h1 className="font-display text-2xl font-bold mb-1">Un repas arrive pour {order.gift_recipient_name || "vous"}{order.is_gift ? " 🎁" : ""}</h1>
      <p className="text-sm text-mute mb-6" data-testid="track-payer-line">
        Commandé par <b className="text-ink">{order.payer_name}</b>{order.payer_country ? ` depuis ${order.payer_country}` : ""} · Livré à <b className="text-ink">{order.gift_recipient_name || "vous"}</b>
      </p>

      {/* 4-digit code — shown to the RECIPIENT here */}
      {order.delivery_code && !["delivered", "cancelled"].includes(order.status) && (
        <div className="bg-gradient-to-br from-brand-yellow/30 to-brand-red/10 border-2 border-brand-yellow rounded-2xl p-5 mb-6 flex items-center gap-4" data-testid="track-code-card">
          <div className="w-14 h-14 rounded-2xl bg-brand-yellow text-ink grid place-items-center shrink-0">
            <KeyRound className="w-7 h-7" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-red font-bold">{t("ts.yourCode")}</div>
            <div className="font-display text-4xl font-bold tracking-[0.4em]" data-testid="track-code-value">{order.delivery_code}</div>
            <div className="text-xs text-mute mt-1">{t("ts.codeHint")}</div>
          </div>
        </div>
      )}

      {/* Status timeline */}
      <div className="bg-white border border-sand rounded-2xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-widest text-mute">{t("ts.status")}</span>
          <span className="rounded-full bg-elevated px-3 py-1 text-sm font-semibold" data-testid="track-status">{t(`status.${order.status}`, STATUS_LABEL[order.status] || order.status)}</span>
        </div>
        <div className="flex items-center gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-2 flex-1 rounded-full ${i <= stepIdx ? "bg-brand-green" : "bg-sand"}`} />
          ))}
        </div>
        {order.eta_minutes && !["delivered", "cancelled"].includes(order.status) && (
          <div className="text-xs text-mute mt-2">Temps estimé : ~{order.eta_minutes} min</div>
        )}
      </div>

      {/* Position du livreur en direct pendant la livraison */}
      {order.driver_location && order.status === "delivering" && (
        <div className="bg-white border border-sand rounded-2xl p-5 mb-6" data-testid="track-live-map-card">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs uppercase tracking-widest text-mute">{t("tracking.driverOnWay")}</span>
            {order.client_gps?.lat != null && (() => {
              const km = toKm([order.driver_location.lat, order.driver_location.lng], [order.client_gps.lat, order.client_gps.lng]);
              return (
                <span className="text-xs font-bold text-brand-red shrink-0" data-testid="track-driver-eta">
                  {t("tracking.driverDistance", { dist: fmtKm(km) })} · {t("tracking.driverEta", { min: etaMin(km) })}
                </span>
              );
            })()}
          </div>
          <DeliveryMap
            driverLoc={order.driver_location}
            clientPos={order.client_gps?.lat != null ? [order.client_gps.lat, order.client_gps.lng] : null}
            height={220}
          />
        </div>
      )}

      <div className="bg-white border border-sand rounded-2xl p-5 space-y-3 text-sm">
        <div className="flex items-start gap-2"><UtensilsCrossed className="w-4 h-4 text-brand-red mt-0.5 shrink-0" /><span>{t("ts.restaurant")} : <b>{order.restaurant_name}</b> · {formatGNF(order.total)}</span></div>
        <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-brand-green mt-0.5 shrink-0" /><span>{order.address}{order.zone ? ` · ${order.zone}` : ""}{order.gift_recipient_landmark ? ` (repère : ${order.gift_recipient_landmark})` : ""}</span></div>
        {order.gift_message && (
          <div className="rounded-xl bg-elevated px-3 py-2 text-mute italic">« {order.gift_message} »</div>
        )}
        <div className="flex items-start gap-2 text-xs text-mute"><Globe2 className="w-4 h-4 mt-0.5 shrink-0" /><span>{t("ts.publicHint")}</span></div>
      </div>
    </div>
  );
}
